/**
 * 데이터 필터링 관련 유틸리티 함수
 * 
 * 사용자, 차단 로그 등의 필터링 로직을 중앙 집중식으로 관리
 */

import { User, BlockedSignupLog } from '@/types';
import { CustomerIdFilter } from './customerIdUtils';

/**
 * 사용자 필터링 옵션
 */
export interface UserFilterOptions {
  query?: string;          // 검색어 (이메일, 이름)
  customerId?: CustomerIdFilter;  // 고객사 ID 필터
  status?: string;         // 상태 필터 ('all', 'active', 'deactivated', 'pending', 'invited')
  role?: string;           // 역할 필터 ('all', 'saas_admin', 'admin', 'user')
}

/**
 * 사용자 목록 필터링
 * 
 * @param users - 필터링할 사용자 배열
 * @param options - 필터링 옵션
 * @returns 필터링된 사용자 배열
 * 
 * @example
 * const filtered = filterUsers(users, {
 *   query: 'john',
 *   customerId: 123,
 *   status: 'active',
 *   role: 'admin'
 * });
 */
export const filterUsers = (
  users: User[],
  options: UserFilterOptions = {}
): User[] => {
  let filtered = users;
  
  // 고객사 필터링
  if (options.customerId !== undefined) {
    if (options.customerId === null) {
      // "(미입력)" 선택: customer_id가 null인 사용자만
      filtered = filtered.filter(user => !user.customer || !user.customer.id);
    } else if (typeof options.customerId === 'number') {
      // 특정 고객사 선택
      filtered = filtered.filter(user => user.customer?.id === options.customerId);
    }
    // undefined이면 필터링하지 않음 (전체)
  }
  
  // 상태 필터링
  if (options.status && options.status !== 'all') {
    filtered = filtered.filter(user => user.status === options.status);
  }
  
  // 역할 필터링
  if (options.role && options.role !== 'all') {
    filtered = filtered.filter(user => user.role === options.role);
  }
  
  // 검색어 필터링 (이메일, 이름)
  if (options.query && options.query.trim()) {
    const lowerQuery = options.query.toLowerCase().trim();
    filtered = filtered.filter(user => {
      const email = user.email?.toLowerCase() || '';
      const name = user.name?.toLowerCase() || '';
      return email.includes(lowerQuery) || name.includes(lowerQuery);
    });
  }
  
  return filtered;
};

/**
 * 차단 로그 필터링 옵션
 */
export interface BlockedLogFilterOptions {
  searchQuery?: string;           // 검색어
  customerFilter?: string;         // 고객사 필터 ('all', 'none', 또는 고객사 ID 문자열)
  customerId?: number | null;      // 현재 사용자의 고객사 ID (권한 체크용)
  isSuperAdmin?: boolean;          // 슈퍼관리자 여부
}

/**
 * 차단 로그 목록 필터링
 * 
 * @param logs - 필터링할 차단 로그 배열
 * @param options - 필터링 옵션
 * @returns 필터링된 차단 로그 배열
 * 
 * @example
 * const filtered = filterBlockedLogs(logs, {
 *   searchQuery: 'test@example.com',
 *   customerFilter: '123',
 *   isSuperAdmin: true
 * });
 */
export const filterBlockedLogs = (
  logs: BlockedSignupLog[],
  options: BlockedLogFilterOptions = {}
): BlockedSignupLog[] => {
  let filtered = logs;
  
  // 보안: 기업 관리자는 자신의 고객사 데이터만 표시
  if (!options.isSuperAdmin && options.customerId !== undefined) {
    filtered = filtered.filter(log => log.customer_id === options.customerId);
  }
  
  // 슈퍼관리자인 경우에만 고객사 필터 적용
  if (options.isSuperAdmin && options.customerFilter && options.customerFilter !== 'all') {
    if (options.customerFilter === 'none') {
      // "(미할당)" 선택: customer_id가 null인 경우
      filtered = filtered.filter(log => log.customer_id === null);
    } else {
      // 특정 고객사 선택
      const filterCustomerId = parseInt(options.customerFilter, 10);
      if (!isNaN(filterCustomerId)) {
        filtered = filtered.filter(log => log.customer_id === filterCustomerId);
      }
    }
  }
  
  // 검색어 필터링
  if (options.searchQuery && options.searchQuery.trim()) {
    const query = options.searchQuery.toLowerCase().trim();
    filtered = filtered.filter((log) => {
      const email = log.email?.toLowerCase() || '';
      const domain = log.domain?.toLowerCase() || '';
      const reason = log.reason?.toLowerCase() || '';
      
      // 보안: 슈퍼관리자인 경우에만 고객사명으로 검색 가능
      if (options.isSuperAdmin) {
        const customerName = log.customer?.customer_name?.toLowerCase() || '';
        return (
          email.includes(query) ||
          domain.includes(query) ||
          customerName.includes(query) ||
          reason.includes(query)
        );
      } else {
        // 기업 관리자는 고객사명 검색 불가
        return (
          email.includes(query) ||
          domain.includes(query) ||
          reason.includes(query)
        );
      }
    });
  }
  
  return filtered;
};

/**
 * 차단 사유 텍스트 변환
 * 
 * @param reason - 차단 사유 코드
 * @returns 사용자 친화적인 차단 사유 텍스트
 */
export const getBlockedReasonText = (reason: string): string => {
  const reasonMap: Record<string, string> = {
    domain_blocked: '차단된 도메인',
    domain_not_allowed: '허용되지 않은 도메인',
    admin_approval_denied: '관리자 승인 거부',
  };
  return reasonMap[reason] || reason;
};

// 하위 호환성을 위한 별칭
export const getReasonText = getBlockedReasonText;

/**
 * 이메일에서 이름 부분 추출
 * 
 * @param email - 이메일 주소
 * @returns 이메일의 로컬 파트 (@ 앞부분)
 * 
 * @example
 * getNameFromEmail('john.doe@example.com') // 'john.doe'
 */
export const getNameFromEmail = (email: string): string => {
  return email.split('@')[0] || email;
};

/**
 * 배열에서 특정 조건을 만족하는 항목 수 반환
 * 
 * @param items - 배열
 * @param predicate - 조건 함수
 * @returns 조건을 만족하는 항목 수
 */
export const countBy = <T>(items: T[], predicate: (item: T) => boolean): number => {
  return items.filter(predicate).length;
};

/**
 * 배열을 특정 필드로 그룹화
 * 
 * @param items - 배열
 * @param keyFn - 그룹화 키를 반환하는 함수
 * @returns 그룹화된 객체
 * 
 * @example
 * const grouped = groupBy(users, user => user.customer_id);
 * // { 1: [user1, user2], 2: [user3], ... }
 */
export const groupBy = <T, K extends string | number>(
  items: T[],
  keyFn: (item: T) => K
): Record<K, T[]> => {
  return items.reduce((acc, item) => {
    const key = keyFn(item);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {} as Record<K, T[]>);
};

